import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pprApi, type PprContact, type PprJob, type PprPosition } from '@/lib/apiClient';

/**
 * Email/notification generator (brief §6.4 + §7).
 *
 * Opens for one contact. Picks a template by position, pre-fills job fields +
 * a deadline (3 business days out, editable), and lets the coordinator copy the
 * subject + body to paste into Outlook. The app never sends mail.
 *
 * Variants:
 *   • Field Leader            → Template A (RedeApp Field Worker Feedback + Good Catch paste area)
 *   • survey positions        → Template B (RedeApp Post Project Survey, pick position)
 *   • Payroll                 → Data Request (job hours / OT / per diem)
 *   • Business Dev/Sales       → Data Request (customer feedback)
 *
 * "Mark as notified" (checked by default for un-notified contacts) flips the
 * contact's NotificationSent when you click Done.
 */

const css = `
  .ppr-em-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1100;
    display: flex; justify-content: center; align-items: flex-start;
    padding: var(--ew-s-6) var(--ew-s-4); overflow-y: auto;
  }
  .ppr-em-modal {
    background: #fff; border-radius: var(--ew-r-lg); max-width: 720px; width: 100%;
    box-shadow: var(--ew-shadow-3); overflow: hidden;
  }
  .ppr-em-header {
    padding: var(--ew-s-4) var(--ew-s-5); background: var(--ew-dark); color: #fff;
    display: flex; justify-content: space-between; align-items: center;
  }
  .ppr-em-header h2 { font: var(--ew-fw-semibold) var(--ew-fs-h5)/1.2 var(--ew-font); }
  .ppr-em-header small { display:block; color: rgba(255,255,255,0.7); font-weight: 400; margin-top: 2px; }
  .ppr-em-close { background: transparent; border: 0; color: #fff; font-size: 22px; cursor: pointer; padding: 0 6px; }
  .ppr-em-body { padding: var(--ew-s-5); max-height: 72vh; overflow-y: auto; display: flex; flex-direction: column; gap: var(--ew-s-3); }
  .ppr-em-footer {
    padding: var(--ew-s-4) var(--ew-s-5); border-top: 1px solid var(--ew-n-200);
    display: flex; align-items: center; gap: var(--ew-s-3); flex-wrap: wrap;
  }
  .ppr-em-footer .spacer { margin-left: auto; }
  .ppr-em-field { display: flex; flex-direction: column; gap: 4px; }
  .ppr-em-field label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase; letter-spacing: var(--ew-ls-caption); color: var(--ew-n-600);
  }
  .ppr-em-row { display: flex; gap: var(--ew-s-3); flex-wrap: wrap; align-items: flex-end; }
  .ppr-em-input, .ppr-em-textarea {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    padding: 8px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    background: #fff; color: var(--ew-dark); width: 100%; box-sizing: border-box;
  }
  .ppr-em-textarea { resize: vertical; }
  .ppr-em-body-text { min-height: 230px; font-family: var(--ew-font); }
  .ppr-em-goodcatch { min-height: 90px; }
  .ppr-em-label-row { display: flex; justify-content: space-between; align-items: center; }
  .ppr-em-copied { color: #027835; font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font); }
  .ppr-em-check { display: inline-flex; align-items: center; gap: 6px; font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1 var(--ew-font); color: var(--ew-dark); }
  .ppr-em-error { color: #8F2414; font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); }
  .ppr-em-hint { color: #8a5a00; font: var(--ew-fw-regular) var(--ew-fs-caption)/1.4 var(--ew-font); margin-top: 4px; }
`;

type Variant = 'fieldLeader' | 'survey' | 'payroll' | 'bizdev';

function variantFor(position: PprPosition): Variant {
  if (position === 'Field Leader') return 'fieldLeader';
  if (position === 'Payroll') return 'payroll';
  if (position === 'Business Development/Sales') return 'bizdev';
  return 'survey';
}

function addBusinessDays(start: Date, days: number): Date {
  const d = new Date(start);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return d;
}

function toInputDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function longDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

function firstNameOf(name: string | null): string {
  if (!name) return '';
  return name.trim().split(/\s+/)[0] ?? '';
}

function buildSubject(variant: Variant, jobName: string, jobNumber: string): string {
  switch (variant) {
    case 'fieldLeader': return `Post Project Review — ${jobName} ${jobNumber} — Field Team Feedback Needed`;
    case 'survey':      return `Post Project Review Survey — ${jobName} ${jobNumber} — Action Required`;
    case 'payroll':     return `Post Project Review — ${jobName} ${jobNumber} — Labor Data Needed`;
    case 'bizdev':      return `Post Project Review — ${jobName} ${jobNumber} — Customer Feedback`;
  }
}

interface BodyCtx {
  greet: string;
  jobName: string;
  jobNumber: string;
  deadline: string;
  goodCatch: string;
}

function buildBody(variant: Variant, c: BodyCtx): string {
  switch (variant) {
    case 'fieldLeader':
      return `${c.greet},

It is my understanding this job closed out. For each of your team members please go to RedeApp → Forms → Post Project Review and Feedback → Field Worker Feedback.

Each person will get separate and individual feedback. See the screenshots below for how to locate the form.

I have provided their Good Catch Submissions below — this information is required on the feedback form.

Good Catch Data — ${c.jobName} ${c.jobNumber}:
${c.goodCatch.trim() || '[Paste the Good Catch data in the field above]'}

You will receive your own feedback in approximately 60–120 days once the financials are completed. Please have your team complete the form by ${c.deadline}. Call me if you have any problems.`;
    case 'survey':
      return `Good morning ${c.greet},

I have been asked to complete the post project review for ${c.jobName} ${c.jobNumber}. Would you please go to RedeApp → Forms → Post Project Survey, select your position from the dropdown, and complete the form.

If you were not involved in this project, just let me know — I am pulling the participant list from our project management system.

Please complete the survey by ${c.deadline}. If you need an extension, let me know.

Thank you,`;
    case 'payroll':
      return `${c.greet},

For the post project review of ${c.jobName} ${c.jobNumber}, could you please provide the job hours, overtime hours, and per diem totals for this job?

Please send them over by ${c.deadline}. Thank you.`;
    case 'bizdev':
      return `${c.greet},

For the post project review of ${c.jobName} ${c.jobNumber}, do you have any customer feedback or a customer feedback report to share?

Please send anything you have by ${c.deadline}. Thank you.`;
  }
}

export default function EmailGeneratorModal({
  job,
  contact,
  onClose,
}: {
  job: PprJob;
  contact: PprContact;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const variant = useMemo(() => variantFor(contact.position), [contact.position]);
  const greet = firstNameOf(contact.contactName) || 'there';

  const [deadlineIso, setDeadlineIso] = useState(() => toInputDate(addBusinessDays(new Date(), 3)));
  const [goodCatch, setGoodCatch] = useState('');
  const [goodCatchTouched, setGoodCatchTouched] = useState(false);
  const [subject, setSubject] = useState(() => buildSubject(variant, job.jobName, job.jobNumber));
  const [body, setBody] = useState('');
  const [markNotified, setMarkNotified] = useState(!contact.notificationSent);
  const [copied, setCopied] = useState<'subject' | 'body' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Regenerate the body whenever the inputs that feed it change. Manual edits to
  // the body persist until one of these changes again.
  useEffect(() => {
    setBody(
      buildBody(variant, {
        greet,
        jobName: job.jobName,
        jobNumber: job.jobNumber,
        deadline: longDate(deadlineIso),
        goodCatch,
      }),
    );
  }, [variant, greet, job.jobName, job.jobNumber, deadlineIso, goodCatch]);

  // mailto: draft link (opens the OS default mail client, e.g. Outlook desktop).
  const mailtoHref = useMemo(() => {
    const to = contact.contactEmail ?? '';
    const qs = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    return `mailto:${to}?${qs}`;
  }, [contact.contactEmail, subject, body]);
  // mailto URLs get truncated by many clients past ~2000 chars (the encoded body
  // inflates length); warn so the coordinator uses Copy for long emails.
  const mailtoTooLong = mailtoHref.length > 1900;

  // Auto-pull Good Catch counts (from the safety-site DB) for the Field Leader
  // email. Pre-fills the paste box once; the coordinator can still edit/override.
  const gcQuery = useQuery({
    queryKey: ['ppr', 'good-catch', job.jobNumber],
    queryFn: () => pprApi.goodCatchByJob(job.jobNumber),
    enabled: variant === 'fieldLeader',
  });
  useEffect(() => {
    if (variant !== 'fieldLeader' || goodCatchTouched) return;
    const data = gcQuery.data;
    if (data?.available && data.people.length > 0) {
      setGoodCatch(
        data.people
          .map((p) => `${[p.firstName, p.lastName].filter(Boolean).join(' ').trim() || 'Unknown'} — ${p.count}`)
          .join('\n'),
      );
    }
  }, [gcQuery.data, variant, goodCatchTouched]);

  const notifyMutation = useMutation({
    mutationFn: () => pprApi.markNotified(job.id, contact.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ppr'] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  async function copy(which: 'subject' | 'body') {
    try {
      await navigator.clipboard.writeText(which === 'subject' ? subject : body);
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setError('Could not access the clipboard — select the text and copy manually.');
    }
  }

  function handleDone() {
    setError(null);
    if (markNotified && !contact.notificationSent) {
      notifyMutation.mutate();
    } else {
      onClose();
    }
  }

  return (
    <>
      <style>{css}</style>
      <div className="ppr-em-backdrop" onClick={onClose}>
        <div className="ppr-em-modal" onClick={(e) => e.stopPropagation()}>
          <div className="ppr-em-header">
            <h2>
              Generate Email
              <small>{contact.position}{contact.contactName ? ` · ${contact.contactName}` : ''} · {job.jobName} {job.jobNumber}</small>
            </h2>
            <button className="ppr-em-close" onClick={onClose} aria-label="Close">×</button>
          </div>

          <div className="ppr-em-body">
            <div className="ppr-em-row">
              <div className="ppr-em-field" style={{ minWidth: 220 }}>
                <label>Deadline</label>
                <input
                  type="date"
                  className="ppr-em-input"
                  value={deadlineIso}
                  onChange={(e) => setDeadlineIso(e.target.value)}
                />
              </div>
              {contact.contactEmail && (
                <div className="ppr-em-field" style={{ flex: 1, minWidth: 220 }}>
                  <label>To</label>
                  <input className="ppr-em-input" readOnly value={contact.contactEmail} />
                </div>
              )}
            </div>

            {variant === 'fieldLeader' && (
              <div className="ppr-em-field">
                <label>Good Catch data (pasted into the email)</label>
                <textarea
                  className="ppr-em-textarea ppr-em-goodcatch"
                  placeholder="Paste the Good Catch submissions for this job…"
                  value={goodCatch}
                  onChange={(e) => { setGoodCatch(e.target.value); setGoodCatchTouched(true); }}
                />
                <div className="ppr-em-hint">
                  {gcQuery.isLoading
                    ? 'Pulling Good Catch totals from Safety…'
                    : gcQuery.data && !gcQuery.data.available
                      ? "Couldn't reach the Safety data — enter Good Catch totals manually."
                      : gcQuery.data && gcQuery.data.people.length === 0
                        ? `No Good Catch submissions found for job ${job.jobNumber} — enter manually if needed.`
                        : gcQuery.data
                          ? `Auto-filled ${gcQuery.data.total} good catch${gcQuery.data.total === 1 ? '' : 'es'} across ${gcQuery.data.people.length} ${gcQuery.data.people.length === 1 ? 'person' : 'people'} from Safety. Edit if needed.`
                          : ''}
                </div>
              </div>
            )}

            <div className="ppr-em-field">
              <div className="ppr-em-label-row">
                <label>Subject</label>
                <button className="ppr-linkbtn" onClick={() => copy('subject')}>
                  {copied === 'subject' ? <span className="ppr-em-copied">Copied!</span> : 'Copy subject'}
                </button>
              </div>
              <input className="ppr-em-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div className="ppr-em-field">
              <div className="ppr-em-label-row">
                <label>Body</label>
                <button className="ppr-linkbtn" onClick={() => copy('body')}>
                  {copied === 'body' ? <span className="ppr-em-copied">Copied!</span> : 'Copy body'}
                </button>
              </div>
              <textarea
                className="ppr-em-textarea ppr-em-body-text"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              {mailtoTooLong && (
                <div className="ppr-em-hint">
                  This email is long — "Open in Outlook" may truncate it. Use "Copy body" and paste instead
                  {variant === 'fieldLeader' ? ' (and attach the screenshots there).' : '.'}
                </div>
              )}
            </div>

            {error && <div className="ppr-em-error">{error}</div>}
          </div>

          <div className="ppr-em-footer">
            <label className="ppr-em-check">
              <input
                type="checkbox"
                checked={markNotified}
                disabled={contact.notificationSent}
                onChange={(e) => setMarkNotified(e.target.checked)}
              />
              {contact.notificationSent ? 'Already marked notified' : 'Mark this contact as notified'}
            </label>
            <a className="btn btn-secondary btn-sm" href={mailtoHref} target="_blank" rel="noopener noreferrer">
              ✉ Open in Outlook
            </a>
            <span className="spacer" />
            <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={notifyMutation.isPending}>
              Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleDone} disabled={notifyMutation.isPending}>
              {notifyMutation.isPending ? 'Saving…' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
